import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useState } from 'react';
import { ExternalLink, ChevronDown } from 'lucide-react';

import { SERVICES_DATA as SERVICES } from '../data/servicesData';

const FAQS = [
  { q:'How long does a website take to build?', a:'A standard business website takes 2–4 weeks. Complex web apps or e-commerce platforms take 4–8 weeks depending on features and scope.' },
  { q:'Do you work with small businesses?', a:'Absolutely. Many of our clients are small businesses and startups. We offer flexible packages to suit different budgets and goals.' },
  { q:'Will my website be mobile-friendly?', a:'Every website we build is fully responsive and mobile-first. Over 70% of web traffic is mobile — we take that seriously.' },
  { q:'Do you provide ongoing maintenance?', a:'Yes. We offer monthly maintenance packages that include updates, backups, performance checks, and priority support.' },
  { q:'How do you handle SEO for new websites?', a:'Every website we build includes basic on-page SEO setup — meta tags, schema, sitemap, and page speed optimization — as standard.' },
];

export default function Services() {
  useScrollReveal();
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <>
      <SEO title="IT Services — Web Development, Design, App, SEO & Digital Marketing" description="CodeViora offers web development, web design, app development, SEO, and digital marketing. End-to-end IT solutions for businesses that want to grow." canonical="/services" />

      {/* Hero */}
      <section className="bg-grid-black pt-24 pb-20 md:pt-32 md:pb-24 relative overflow-hidden">
        <div className="container-max relative">
          <div className="max-w-3xl animate-fade-up">
            <span className="section-label-inv">Our Services</span>
            <h1 className="section-title-inv text-5xl md:text-6xl mt-2">EVERYTHING YOUR BUSINESS NEEDS ONLINE.</h1>
            <p className="section-sub-inv text-lg">Web development, design, app development, SEO, and digital marketing — all under one roof.</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/contact" className="btn-primary-inv">Get Free Quote</Link>
              <Link to="/about" className="btn-outline-inv">About Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid (Matching Portfolio Style) */}
      <section className="bg-grid-white py-20">
        <div className="container-max">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {SERVICES.map(({ slug, icon, title, headline, desc, img, imgAlt, tag }) => (
              <Link key={slug} to={`/services/${slug}`} className="group cursor-pointer bg-black/5 dark:bg-white/5 dark:bg-[#111] border border-black/10 dark:border-white/10 dark:border-white/10 rounded-3xl p-6 hover:border-black dark:border-white dark:hover:border-white/40 transition-all duration-300 flex flex-col">
                <div className="relative overflow-hidden rounded-2xl mb-6 aspect-[16/9] bg-black/5 dark:bg-white/5">
                  <img 
                    src={img} 
                    alt={imgAlt} 
                    className="img-reveal w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/40 dark:bg-white/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="w-12 h-12 bg-white dark:bg-[#111] rounded-full flex items-center justify-center text-black dark:text-white transform scale-50 group-hover:scale-100 transition-transform duration-300 delay-100 shadow-xl">
                      <ExternalLink size={20} />
                    </div>
                  </div>
                  {tag && (
                    <div className="absolute top-4 right-4 bg-white dark:bg-[#111] text-black dark:text-white px-3 py-1 rounded-full text-xs font-medium shadow-lg">
                      {tag}
                    </div>
                  )}
                </div>
                <div className="flex flex-col flex-1">
                  <div className="flex items-center gap-3 mb-4 text-black dark:text-white">
                    <span className="p-2 bg-black/5 dark:bg-white/5 dark:bg-white/10 rounded-xl">{icon}</span>
                    <h3 className="text-2xl font-medium font-display">{title}</h3>
                  </div>
                  <h4 className="text-lg font-medium text-black dark:text-white mb-3 line-clamp-2">{headline}</h4>
                  <p className="text-sm text-black/60 dark:text-white/60 dark:text-white/60 leading-relaxed mb-6 flex-1 line-clamp-3">{desc}</p>
                  <div className="mt-auto flex items-center text-[#ff6b2b] font-medium text-sm">
                    Learn more &rarr;
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max max-w-3xl">
          <div className="reveal text-center mb-12">
            <span className="section-label">FAQ</span>
            <h2 className="section-title">COMMON QUESTIONS.</h2>
          </div>
          <div className="flex flex-col gap-3">
            {FAQS.map(({ q, a }, i) => (
              <div key={q} className="reveal card-white overflow-hidden" style={{transitionDelay:`${i*60}ms`}}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 p-7 text-left group"
                  aria-expanded={openFaq === i}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-black/40 dark:text-white/40 dark:text-white/40 text-sm flex-shrink-0 mt-0.5">Q.</span>
                    <span className="font-medium text-black dark:text-white text-sm">{q}</span>
                  </div>
                  <span className={`w-7 h-7 rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-300 text-black dark:text-white ${openFaq === i ? 'bg-black text-white dark:bg-white dark:text-black border-transparent rotate-45' : 'border-black/15 dark:border-white/15 dark:border-white/15'}`}>
                    <ChevronDown size={14} />
                  </span>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openFaq === i ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="text-sm text-black/60 dark:text-white/60 dark:text-white/60 leading-relaxed px-7 pb-7 pl-12">{a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-pure-black py-20 border-t border-white/10">
        <div className="container-max text-center reveal">
          <h2 className="text-4xl md:text-5xl text-white mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>LET'S BUILD SOMETHING.</h2>
          <p className="text-white/40 text-lg mb-8 max-w-lg mx-auto">Tell us what you need. We'll get back within 24 hours with a free consultation and quote.</p>
          <Link to="/contact" className="btn-primary-inv">
            Get Free Quote
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none" className="ml-2 inline"><path d="M3 9H15M11 5l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </Link>
        </div>
      </section>
    </>
  );
}
